import {lineAnchorFrom, lineOrientationFrom} from '@github-ui/diffs/diff-line-helpers'
import {memo, useMemo} from 'react'

import {getLineHunkData, type HunkData} from '../helpers/hunk-data-helpers'
import {getDiffLineMarkersKey, rowIdFrom} from '../helpers/line-helpers'
import type {DiffLine, FileDiffReference, LineKey} from '../types'
import {CodeDiffLine} from './DiffLineTableRow'
import ExpandableHunkHeaderDiffLine from './ExpandableHunkHeaderDiffLine'
import {hasHiddenUnicodeCharacters, showHiddenUnicodeCharactersRaw} from '@github-ui/hidden-unicode-banner/utils'
import {showHiddenUnicodeCharactersHTML} from '@github-ui/hidden-unicode-banner/HiddenUnicodeCharacter'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useDiffAnchor, useDiffLineSelectionInfo, useFileLineCount} from '../stores/selectors'
import {useMarkersAtPosition} from '@github-ui/pull-requests/pull-request-store-selectors'
import type {ViewerData} from '../conversation-types'

const UnifiedDiffRow = memo(function UnifiedDiffRow({
  copilotChatReference,
  diffHasHiddenUnicodeCharacters,
  filePath,
  hunksData,
  currentDiffLine,
  nextDiffLine,
  prevDiffLine,
  showHiddenUnicode,
  subjectId,
  viewerData,
}: {
  copilotChatReference?: FileDiffReference
  diffHasHiddenUnicodeCharacters: boolean
  filePath: string
  hunksData: HunkData[]
  currentDiffLine: DiffLine
  nextDiffLine: DiffLine | undefined
  prevDiffLine: DiffLine | undefined
  showHiddenUnicode: boolean
  subjectId: string
  viewerData: ViewerData
}) {
  const diffAnchor = useDiffAnchor()
  const fileLineCount = useFileLineCount()

  const currentDiffLineSelectionInfo = useDiffLineSelectionInfo(currentDiffLine)

  let currentDiffLineWithLineSelectionInfo = useMemo(
    () => ({
      ...currentDiffLine,
      ...currentDiffLineSelectionInfo,
    }),
    [currentDiffLine, currentDiffLineSelectionInfo],
  )

  const isRowSelected = useMemo(
    () => (currentDiffLineWithLineSelectionInfo.isSelected ? true : false),
    [currentDiffLineWithLineSelectionInfo.isSelected],
  )
  const lineSide = currentDiffLine.type !== 'DELETION' ? 'RIGHT' : 'LEFT'
  const lineNumber = lineSide === 'RIGHT' ? currentDiffLine.right : currentDiffLine.left
  const lineMarkersKey = getDiffLineMarkersKey(lineNumber, lineSide)
  const {
    threads: threadsAtPosition,
    annotations: annotationsAtPosition,
    commentsPreference,
  } = useMarkersAtPosition(filePath, lineMarkersKey ?? ('' as LineKey))

  const isHunkRow = currentDiffLineWithLineSelectionInfo.type === 'HUNK'
  const lineAnchor = lineAnchorFrom(
    diffAnchor,
    lineOrientationFrom(currentDiffLineWithLineSelectionInfo.type),
    currentDiffLineWithLineSelectionInfo.blobLineNumber,
  )
  const {currentHunk, nextHunk, previousHunk} = getLineHunkData(currentDiffLineWithLineSelectionInfo, hunksData)
  // Use original line here since it is a deep comparison
  const rowId = rowIdFrom(diffAnchor, currentDiffLineWithLineSelectionInfo, currentDiffLineWithLineSelectionInfo)

  const lineHasHiddenUnicodeCharacters =
    diffHasHiddenUnicodeCharacters && hasHiddenUnicodeCharacters(currentDiffLineWithLineSelectionInfo.text)

  if (showHiddenUnicode && lineHasHiddenUnicodeCharacters) {
    if (currentDiffLineWithLineSelectionInfo.html) {
      // eslint-disable-next-line react-hooks/immutability
      currentDiffLineWithLineSelectionInfo = {
        ...currentDiffLineWithLineSelectionInfo,
        html:
          showHiddenUnicodeCharactersHTML(currentDiffLineWithLineSelectionInfo.html as SafeHTMLString) ??
          currentDiffLineWithLineSelectionInfo.html,
      }
    } else {
      currentDiffLineWithLineSelectionInfo = {
        ...currentDiffLineWithLineSelectionInfo,
        text: showHiddenUnicodeCharactersRaw(currentDiffLineWithLineSelectionInfo.text),
      }
    }
  }

  const isLeftSide = currentDiffLineWithLineSelectionInfo.type === 'DELETION'

  return (
    <tr key={rowId} className="diff-line-row">
      {isHunkRow ? (
        <ExpandableHunkHeaderDiffLine
          currentHunk={currentHunk}
          diffAnchor={diffAnchor}
          diffLine={currentDiffLineWithLineSelectionInfo}
          fileLineCount={fileLineCount}
          isLeftSide={isLeftSide}
          isSplit={false}
          nextHunk={nextHunk}
          nextLine={nextDiffLine as DiffLine}
          previousHunk={previousHunk}
          prevLine={prevDiffLine as DiffLine}
        />
      ) : (
        <CodeDiffLine
          annotationsAtPosition={annotationsAtPosition}
          commentsPreference={commentsPreference}
          copilotChatReference={copilotChatReference}
          currentHunk={currentHunk}
          diffAnchor={diffAnchor}
          diffLine={currentDiffLineWithLineSelectionInfo}
          filePath={filePath}
          hasHiddenUnicodeCharacters={lineHasHiddenUnicodeCharacters}
          isLeftSide={isLeftSide}
          isRowSelected={isRowSelected}
          isSplit={false}
          lineAnchor={lineAnchor}
          nextHunk={nextHunk}
          previousHunk={previousHunk}
          rowId={rowId}
          subjectId={subjectId}
          threadsAtPosition={threadsAtPosition}
          viewerData={viewerData}
        />
      )}
    </tr>
  )
})

export default UnifiedDiffRow

try{ UnifiedDiffRow.displayName ||= 'UnifiedDiffRow' } catch {}